import React, { useState } from 'react';
import classes from './Gallery.module.css';
import { useNavigate } from 'react-router-dom';
import usePreviewList from '../../CustomHooks/usePreviewList';
import SectionTitle from '../../ReusableComponents/SectionTitle/SectionTitle';
import Reveal from '../../ReusableComponents/Reveal/Reveal';

const selectAlbums = (result) => result?.data;

const photoCount = (album) => album?.uploadDoc?.length ?? 0;

function Gallery() {
    const navigate = useNavigate();
    const [selectedId, setSelectedId] = useState(null);

    // Landing page shows the first 6 albums; the rest live behind SEE ALL.
    const { items: albums, loading } = usePreviewList({
        endpoint: "/album",
        method: "get",
        selectItems: selectAlbums,
    });

    // Derived rather than synced in an effect: the first album is selected on
    // the very render the albums arrive, with no in-between paint showing
    // folders but no photos.
    const selectedAlbum =
        albums.find((album) => album.id === selectedId) ?? albums[0] ?? null;

    const photos = selectedAlbum?.uploadDoc ?? [];

    return (
        <section className={classes.gallerySection} id='gallery'>
            <div className={classes.inner}>
                <SectionTitle
                    eyebrow="In pictures"
                    title={'Gallery'}
                    seeAll={albums.length > 0}
                    onclick={() => navigate('/all-album')}
                />

                {!loading && albums.length === 0 ? (
                    <p className={classes.empty}>No albums published yet.</p>
                ) : (
                    <div className={classes.galleryContainer}>
                        {/* Folders as cover cards. Replaces the pill strip, which
                            overflowed badly on the long album titles the API
                            returns, and gave no sense of what was inside. */}
                        <Reveal
                            className={classes.folders}
                            selector="[data-folder]"
                            stagger={0.07}
                            y={28}
                            role="tablist"
                            aria-label="Photo albums"
                        >
                            {albums.map((album) => {
                                const active = selectedAlbum?.id === album.id;
                                const cover = album?.uploadDoc?.[0]?.url;
                                const count = photoCount(album);

                                return (
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        onClick={() => setSelectedId(album.id)}
                                        key={album?.id ?? album?.title}
                                        className={`${classes.folder} ${active ? classes.active : ''}`}
                                        data-folder
                                    >
                                        <span className={classes.folderCover}>
                                            {cover ? (
                                                <img src={cover} alt="" loading="lazy" />
                                            ) : (
                                                <span className={classes.folderCoverFallback} aria-hidden="true" />
                                            )}
                                        </span>
                                        <span className={classes.folderMeta}>
                                            <span className={classes.folderName}>{album?.title}</span>
                                            <span className={classes.folderCount}>
                                                {count} {count === 1 ? 'photo' : 'photos'}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </Reveal>

                        <Reveal
                            className={classes.bottomContainer}
                            selector="[data-photo]"
                            stagger={0.06}
                            y={30}
                            scale={0.96}
                            /* Re-keyed per album so switching folders replays the reveal. */
                            key={selectedAlbum?.id ?? 'empty'}
                        >
                            {photos.slice(0, 8).map((photo, idx) => (
                                <figure className={classes.photo} key={photo?.id ?? idx} data-photo>
                                    <img
                                        src={photo?.url}
                                        alt={`${selectedAlbum?.title ?? 'Gallery'} ${idx + 1}`}
                                        loading="lazy"
                                    />
                                </figure>
                            ))}
                        </Reveal>
                    </div>
                )}
            </div>
        </section>
    )
}

export default Gallery
